// ---------------------------------------------------------------------------
// CogoKnowledgeRepository — domain contract for the Cogo KG.
//
// Source: .planning/kg-repository-interface.md §3 (v0.2) + §12 (v1.0 lock).
//
// Key design points (v1.0):
//   - framework-agnostic (no React / no browser/node split)
//   - Result<T> wrap for async methods that benefit from meta (durationMs,
//     source). Sync housekeeping (versionInfo) returns plain values.
//   - SHACL store-scoped: validate(phase) runs the entire phase's rule set
//     against the currently-loaded Oxigraph store. Caller is responsible
//     for loading target JSON via loadJsonLd(graph) + dropping via
//     dropGraph(graph) — Option A (§12.4 Q8).
//   - Write path stays server-only until Phase 2c (Data Symbol).
// ---------------------------------------------------------------------------

import type {
  Action,
  DesignSystem,
  Event,
  Example,
  FindObservationsQuery,
  I18n,
  Narrative,
  Observation,
  ObservationRecord,
  Pattern,
  Result,
  SaveResult,
  SemanticType,
  SyncResult,
  ThemeV2Record,
  Tokens,
  ValidationPhase,
  ValidationReport,
} from "./types"
import type {
  ExpressionAccessorItem,
  ExpressionPrefixItem,
  UiActionVocabularyItem,
  UiEventVocabularyItem,
} from "./vocabulary"

export interface FindExamplesQuery {
  /**
   * cogo:SemanticType IRI — the ctype: instance IRI the example's root
   * primitive declares (e.g. "https://cogo.xyz/instances/semantic-type/card").
   * Made optional at v1.0.8 so block-level consumers (Studio block-editor
   * inspector, agentic Judge block retrieval) can query by pattern alone.
   * At least one of `semanticType` or `pattern` MUST be provided — the
   * runtime rejects queries that pass neither with an empty-data Result.
   */
  semanticType?: string
  /**
   * cogo:Pattern IRI — the cpat: composition-archetype IRI the example
   * realizes (e.g. "https://cogo.xyz/instances/pattern/auth-form"). Wired
   * to the SPARQL filter at v1.0.8; was declared-but-unused in v1.0.0-v1.0.7.
   * Accepts either a bare cpat:<id> IRI string or the short pattern id
   * (e.g. "auth-form") — the repository expands short ids against the
   * cpat: namespace automatically.
   */
  pattern?: string
  /** cogo:designSystemId literal. */
  dsId?: string
  /** Per-variant examples (future — not in L3 today). */
  variant?: string
  /** Top-K, default 5. */
  k?: number
}

export interface GetActionsFilter {
  category?: string
  tier?: string
  /** Filter by cogo:platforms membership, e.g. ["web"]. */
  platforms?: string[]
  /** Limit to specific Action IRIs. */
  ids?: string[]
}

export interface GetEventsFilter {
  domain?: string
  /** SemanticType IRI to match cogo:emittedByComponent. */
  emittedByComponent?: string
  /** DatasourceType IRI to match cogo:emittedByDatasource. */
  emittedByDatasource?: string
  /** Action IRI to match cogo:emittedByAction. */
  emittedByAction?: string
}

/**
 * SHACL rule registry.
 *
 * Repository init resolves a registry (either injected prebuilt rules or
 * a future runtime SHACL→SPARQL compiler). `rulesFor(phase)` returns the
 * full rule list for a phase — caller never authors rules directly.
 *
 * See §4a of the design doc for the rule shape + Option A store-scoping
 * pattern.
 */
export interface ShaclRuleRegistry {
  rulesFor(phase: ValidationPhase): import("@cogo/kg-browser").ShaclQueryRule[]
}

/**
 * Minimal `ShaclRuleRegistry` impl backed by a plain
 * `{ [phase]: ShaclQueryRule[] }` map. Consumers (cogo-studio,
 * cogo-starter, cogo-commerce) ship their own rule tables and wrap them
 * here before passing into `LocalWasmRepositoryConfig.ruleRegistry`.
 *
 * Build-time SHACL→SPARQL compiler (Phase 2 Step 5.1 #6) will eventually
 * generate the map from `cogo-ns-v1.ttl` shapes automatically; until then
 * rules live in handwritten tables in each consumer.
 *
 * Example:
 * ```ts
 * import { MapBackedRuleRegistry, type ShaclRuleRegistry } from '@cogo/kg-repository'
 *
 * const registry: ShaclRuleRegistry = new MapBackedRuleRegistry({
 *   structural: STUDIO_STRUCTURAL_RULES,
 *   binding:    STUDIO_BINDING_RULES,
 * })
 * ```
 */
export class MapBackedRuleRegistry implements ShaclRuleRegistry {
  constructor(
    private readonly map: Partial<
      Record<
        ValidationPhase,
        readonly import("@cogo/kg-browser").ShaclQueryRule[]
      >
    > = {},
  ) {}

  rulesFor(phase: ValidationPhase): import("@cogo/kg-browser").ShaclQueryRule[] {
    const rules = this.map[phase]
    return rules ? [...rules] : []
  }
}

/**
 * Optional store helpers for the Option A validate pattern.
 *
 * Callers that want to validate a specific JSON payload (vs the entire
 * already-loaded store) call `loadJsonLd` → `validate(phase)` → `dropGraph`
 * in sequence. Implementations that don't expose store management (e.g.
 * ServerHttpRepository) MAY no-op these and require callers to pre-load
 * via a separate flow.
 */
export interface StoreScopedValidation {
  /**
   * Insert JSON-LD triples into a named graph so SHACL rules can SELECT
   * against them. Returns the graph IRI for later {@link dropGraph}.
   */
  loadJsonLd(jsonLd: unknown, graphIri?: string): Promise<string>
  /** Remove all triples in a previously-loaded named graph. */
  dropGraph(graphIri: string): Promise<void>
}

export interface CogoKnowledgeRepository {
  // ——— Read (hot path) ———

  findExamples(q: FindExamplesQuery): Promise<Result<Example[]>>

  getDesignSystem(id: string): Promise<Result<DesignSystem | null>>
  listDesignSystems(): Promise<Result<DesignSystem[]>>

  getSemanticTypes(
    ctx: { category?: string },
  ): Promise<Result<SemanticType[]>>

  /**
   * cogo:Narrative — DS component F. Returns brand-level narrative metadata
   * for a given dsId, or null when no narrative-v1.ttl instance matches.
   *
   * Local-path (LocalWasmRepository): SPARQL against cnarr:{dsId} shape.
   * Fallback: repositories MAY short-circuit to local only; server fallback
   * is optional in v1 (DS narratives are additive context, not blocking).
   */
  getNarrative(
    ctx: { dsId: string },
  ): Promise<Result<Narrative | null>>

  /**
   * cogo:I18n — DS component J. Returns the locale-aware rendering shape
   * whose `supportedLocales` contains the requested BCP-47 tag, or null when
   * no i18n-v1.ttl instance matches.
   *
   * Local-path (LocalWasmRepository): SPARQL against ci18n:{id} instances.
   * Fallback: repositories MAY short-circuit to local only; server fallback
   * is optional in v1 (i18n shapes are additive context, not blocking).
   */
  getI18n(
    ctx: { locale: string },
  ): Promise<Result<I18n | null>>

  /**
   * cogo:Tokens — DS component A. Returns the per-DS token set
   * (spacing / elevation / motion / breakpoints + colors / radius / typography)
   * for the given id, or null when no tokens-v1.ttl instance matches.
   *
   * Local-path (LocalWasmRepository): SPARQL against ctok:{id} instances.
   * Fallback: repositories MAY short-circuit to local only; server fallback
   * is optional in v1 (Tokens are additive rendering hints, not blocking).
   */
  getTokens(
    ctx: { id: string },
  ): Promise<Result<Tokens | null>>

  /**
   * Compose a full ThemeV2 record from KG by fetching the referenced
   * primitives (Tokens), atom defaults, narrative, and i18n in parallel.
   * Returns the assembled record with version pinned to '2.0.0'.
   *
   * v1.0.10 — Theme is a composite (not a standalone TTL row). Local-path
   * implementation fans out to `getTokens` / `getPatterns` (atom-defaults
   * Pattern) / `getNarrative` / `getI18n` and re-assembles the shape. Misses
   * on any individual sub-fetch degrade to the matching optional field being
   * undefined or empty rather than failing the compose — the `$id` +
   * `version` envelope is always emitted.
   */
  getTheme(ctx: { themeId: string }): Promise<{ theme: ThemeV2Record }>

  /**
   * cogo:Pattern — DS component D. Returns the list of Pattern instances,
   * optionally filtered by `domain`. `domain` is a case-insensitive
   * substring match against `domainSemantics` today (v1 has no structured
   * `cogo:domain` predicate — deferred to v1.0.X per spec §12 Q3).
   *
   * Local-path (LocalWasmRepository): SPARQL against cpat:* instances.
   * Fallback: repositories MAY short-circuit to local only; server
   * fallback is optional in v1 (Patterns are additive composition hints,
   * not blocking).
   */
  getPatterns(
    ctx: { domain?: string },
  ): Promise<Result<Pattern[]>>

  getActions(filter: GetActionsFilter): Promise<Result<Action[]>>

  getEvents(filter: GetEventsFilter): Promise<Result<Event[]>>

  // ——— Vocabulary read surface (Studio Vocabulary panel) ———
  //
  // OPTIONAL: migrated from cogo-studio kg-client's localGet* bypass so the
  // SPARQL lives behind the Repository. Optional (not required) because the
  // many test fakes typed `: CogoKnowledgeRepository` across packages don't
  // need a vocabulary surface; LocalWasmRepository and HybridRepository
  // implement all four.

  getExpressionPrefixes?(): Promise<Result<ExpressionPrefixItem[]>>

  getExpressionAccessors?(): Promise<Result<ExpressionAccessorItem[]>>

  getUiEventVocabulary?(): Promise<Result<UiEventVocabularyItem[]>>

  getUiActionVocabulary?(): Promise<Result<UiActionVocabularyItem[]>>

  // ——— Validation (store-scoped) ———

  validate(phase: ValidationPhase): Promise<Result<ValidationReport>>

  // ——— Housekeeping (sync / non-meta) ———

  versionInfo(): {
    coreVersion: string
    oxigraphVersion: string
    shapeSha: string
  }
  ready(): Promise<void>

  // ——— Write (cold path — Phase 2c) ———

  // Replaces v1 stub — v2 shape per Gap 2 spec §3.4
  recordObservation(
    obs: Observation,
  ): Promise<Result<{ id: string }>>
  syncObservations(): Promise<Result<SyncResult>>
  saveObservations(): Promise<Result<SaveResult>>
  findObservations(
    query?: FindObservationsQuery,
  ): Promise<Result<ObservationRecord[]>>
  registerExample(
    args: {
      template: unknown
      bindingSchema: object
      semanticType: string
      dsId?: string
    },
  ): Promise<Result<{ exampleId: string }>>
  upsertDesignSystem(
    ds: DesignSystem,
  ): Promise<Result<{ dsId: string }>>
}
